import { useFpB2bProductsInfo } from '@cart-services/hooks/fp-b2b/use-fp-b2b-products-info'
import { type CartItem } from '@cart-services/types'
import { Checkbox, type CheckboxProps } from '@pets-ui/Checkbox/Checkbox'
import { Collapse } from '@pets-ui/Collapse/Collapse'
import { Drawer } from '@pets-ui/Drawer/Drawer'
import { Footer } from '@pets-ui/Footer/Footer'
import { HStack } from '@pets-ui/HStack/HStack'
import { IconButton } from '@pets-ui/IconButton/IconButton'
import { ArrowUpSmall } from '@pets-ui/icons'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { useResponsive } from '@pets-ui/useResponsive/useResponsive'
import * as stylex from '@stylexjs/stylex'
import { useMemo, useState } from 'react'
import { isShortage } from '../../utils/isShortage'
import { useCart } from '../CartProvider/CartProvider'
import { FooterReminderContent } from './FooterReminderContent'

const styles = stylex.create({
  open: {
    overflow: 'hidden',
  },
  checkAllLabel: {
    cursor: 'pointer',
    userSelect: 'none',
  },
  content: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
  },
  arrow: {
    transition: 'transform 0.3s',
  },
  arrowOpen: {
    transform: 'rotate(180deg)',
  },
})

export interface FooterReminderProps {
  cartItems?: CartItem[]
  onCheckAllChange?: CheckboxProps['onChange']
}

export const FooterReminder = (props: FooterReminderProps) => {
  const { cartItems, onCheckAllChange } = props
  const isPhone = useResponsive('mobile')
  const [open, setOpen] = useState(false)
  const { projectCode, countryCode, campaignCodeFromUrl, campaignId, platform } = useCart()
  const { productMap } = useFpB2bProductsInfo({ projectCode, countryCode, campaignCode: campaignCodeFromUrl, campaignId, platform })
  const nonDeletedProducts = useMemo(() => cartItems?.filter(item => !item.butler.isDeleted), [cartItems])
  const shortageProducts = useMemo(() => {
    if (!productMap) return undefined
    return nonDeletedProducts?.filter(item => isShortage(item.quantity, Number(productMap[item.variation_id]?.stock)))
  }, [nonDeletedProducts, productMap])
  const total = nonDeletedProducts?.length || 0
  const shortage = shortageProducts?.length || 0
  const totalChecked = nonDeletedProducts?.filter(item => item.butler.checked).length || 0
  const hasShortage = shortage > 0

  const handleToggle = () => {
    setOpen(v => !v)
  }

  const handleCheckAll: CheckboxProps['onChange'] = (e) => {
    onCheckAllChange?.(e)
  }

  const shouldOpen = open && hasShortage

  return (
    <Footer rootStyle={shouldOpen && styles.open} ContainerProps={{ flexDirection: 'column' }}>
      {!isPhone
        ? (
          <Collapse in={shouldOpen}>
            <FooterReminderContent shortageProducts={shortageProducts} />
          </Collapse>
        )
        : (
          <Drawer
            direction="bottom"
            open={shouldOpen}
            onClose={handleToggle}
          >
            <FooterReminderContent shortageProducts={shortageProducts} titleAlign="center" maxHeight={200} />
          </Drawer>
        )}
      <div {...stylex.props(styles.content)}>
        <label {...stylex.props(styles.checkAllLabel)}>
          <HStack alignItems="center" gap={2}>
            <Checkbox
              checked={total === totalChecked}
              indeterminate={totalChecked > 0 ? total - totalChecked > 0 : undefined}
              onChange={handleCheckAll}
            />
            <Text color={colors.grey800}>
              全部商品無誤 (
              {total}
              )
            </Text>
          </HStack>
        </label>
        <HStack alignItems="center" gap={2}>
          <Text color={hasShortage ? colors.red400 : colors.grey600}>
            缺貨品項
          </Text>
          <IconButton disabled={!hasShortage} onClick={handleToggle}>
            <ArrowUpSmall rootStyle={[styles.arrow, shouldOpen && styles.arrowOpen]} />
          </IconButton>
        </HStack>
      </div>
    </Footer>
  )
}
